import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { BRAND } from './sltUi';
import logo from '../classlabs.png';

const NAV_ITEMS = [
  { to: '/slt/dashboard', label: 'Overview', icon: '◧' },
  { to: '/slt/teachers', label: 'Teachers', icon: '▤' },
  { to: '/slt/students', label: 'Students', icon: '▦' }
];

// Persistent shell for every SLT page: fixed sidebar navigation with active
// states + user identity, and a main column with breadcrumb + page header.
const SLTLayout = ({ user, onLogout, title, subtitle, breadcrumbs = [], actions = null, children }) => {
  const domain = String(user?.email || '').split('@')[1] || '';

  return (
    <div style={{ background: BRAND.cream, color: BRAND.charcoal, display: 'flex', fontFamily: "'Inter', sans-serif", minHeight: '100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Nunito:wght@700;800&display=swap');
        h1, h2, h3 { font-family: 'Nunito', sans-serif; }
        .slt-nav-link { border-radius: 10px; color: ${BRAND.charcoal}; display: flex; align-items: center; gap: 10px;
          font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.98rem; padding: 11px 14px; text-decoration: none; }
        .slt-nav-link:hover { background: rgba(117, 185, 190, 0.15); }
        .slt-nav-link.active { background: ${BRAND.charcoal}; color: ${BRAND.white}; }
        .slt-row:hover { background: rgba(117, 185, 190, 0.08); }
        .slt-row.clickable { cursor: pointer; }
        .slt-row.clickable:focus { outline: 2px solid ${BRAND.coral}; outline-offset: -2px; }
        .slt-crumb { color: ${BRAND.muted}; text-decoration: none; font-weight: 700; }
        .slt-crumb:hover { color: ${BRAND.coral}; }
        @media (max-width: 860px) {
          .slt-sidebar { display: none !important; }
        }
      `}</style>

      <aside className="slt-sidebar" style={{ background: BRAND.white, borderRight: `2px solid ${BRAND.charcoal}`, display: 'flex', flexDirection: 'column', flexShrink: 0, padding: '26px 16px', position: 'sticky', top: 0, height: '100vh', width: '230px', boxSizing: 'border-box' }}>
        <div style={{ marginBottom: '30px', padding: '0 6px' }}>
          <img
            src={logo}
            alt="ClassLabs"
            style={{ display: 'block', height: 'auto', maxWidth: '160px', mixBlendMode: 'multiply', objectFit: 'contain', width: '100%' }}
          />
          <div style={{ background: 'rgba(255, 127, 80, 0.14)', border: `1px solid ${BRAND.coral}`, borderRadius: '999px', color: BRAND.charcoal, display: 'inline-block', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.06em', marginTop: '8px', padding: '3px 10px', textTransform: 'uppercase' }}>
            SLT Dashboard
          </div>
        </div>

        <nav aria-label="SLT sections" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} end className={({ isActive }) => `slt-nav-link${isActive ? ' active' : ''}`}>
              <span aria-hidden="true">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div style={{ borderTop: '1.5px solid rgba(45,64,66,0.15)', marginTop: 'auto', paddingTop: '16px' }}>
          <div style={{ fontSize: '0.88rem', fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : (user?.email || 'SLT user')}
          </div>
          {domain && <div style={{ color: BRAND.muted, fontSize: '0.78rem', marginTop: 2 }}>@{domain}</div>}
          <button
            type="button"
            onClick={onLogout}
            style={{ background: BRAND.white, border: `2px solid ${BRAND.charcoal}`, borderRadius: '10px', color: BRAND.charcoal, cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontWeight: 800, marginTop: '12px', padding: '8px 14px', width: '100%' }}
          >
            Log out
          </button>
        </div>
      </aside>

      <main style={{ boxSizing: 'border-box', flex: 1, minWidth: 0, padding: '32px 30px 60px' }}>
        <div style={{ margin: '0 auto', maxWidth: '1150px' }}>
          {breadcrumbs.length > 0 && (
            <nav aria-label="Breadcrumb" style={{ fontSize: '0.85rem', marginBottom: '14px' }}>
              {breadcrumbs.map((crumb, idx) => (
                <span key={idx}>
                  {idx > 0 && <span style={{ color: BRAND.muted, margin: '0 7px' }}>/</span>}
                  {crumb.to
                    ? <Link className="slt-crumb" to={crumb.to}>{crumb.label}</Link>
                    : <span style={{ color: BRAND.charcoal, fontWeight: 800 }}>{crumb.label}</span>}
                </span>
              ))}
            </nav>
          )}

          <header style={{ alignItems: 'flex-end', display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-between', marginBottom: '24px' }}>
            <div>
              <h1 style={{ fontSize: '1.9rem', lineHeight: 1.1, margin: 0 }}>{title}</h1>
              {subtitle && <p style={{ color: BRAND.muted, fontSize: '0.98rem', margin: '8px 0 0' }}>{subtitle}</p>}
            </div>
            {actions}
          </header>

          {children}
        </div>
      </main>
    </div>
  );
};

export default SLTLayout;
